import { useTheme } from "@mui/material/styles";

/**
 * Hook to get theme-aware styling that only applies in dark mode
 * @param {Object} darkModeStyles - Styles to apply only in dark mode
 * @param {Object} lightModeStyles - Styles to apply in light mode (optional)
 * @returns {Object} Conditional styles based on theme mode
 */
export const useThemeAwareStyles = (darkModeStyles, lightModeStyles = {}) => {
  const theme = useTheme();

  return theme.palette.mode === "dark" ? darkModeStyles : lightModeStyles;
};

/**
 * Get text color that respects theme mode
 * @param {Object} theme - Material-UI theme object
 * @param {string} darkModeColor - Color to use in dark mode
 * @param {string} lightModeColor - Color to use in light mode (optional, defaults to theme default)
 * @returns {Object} sx prop object with conditional color
 */
export const getThemeTextColor = (theme, darkModeColor = "text.primary", lightModeColor = null) => {
  if (theme.palette.mode === "dark") {
    return { color: darkModeColor };
  }

  return lightModeColor ? { color: lightModeColor } : {};
};

/**
 * Get background color that respects theme mode
 * @param {Object} theme - Material-UI theme object
 * @param {string} darkModeColor - Background color to use in dark mode
 * @param {string} lightModeColor - Background color to use in light mode (optional, defaults to theme default)
 * @returns {Object} sx prop object with conditional background color
 */
export const getThemeBackgroundColor = (theme, darkModeColor = "background.paper", lightModeColor = null) => {
  if (theme.palette.mode === "dark") {
    return { backgroundColor: darkModeColor };
  }

  return lightModeColor ? { backgroundColor: lightModeColor } : {};
};

/**
 * Get hover text color for colored DataGrid rows that respects theme mode
 * @param {Object} theme - Material-UI theme object
 * @returns {string} Color value for hover state
 */
export const getHoverTextColor = (theme) => {
  return theme.palette.mode === "dark" ? "#ffffff !important" : "#000000 !important";
};
